// Fetch wrapper for all Netlify Function calls. Attaches the Firebase ID token,
// normalizes the { ok, data | error } envelope, and throws a typed ApiError.
import { auth } from '../lib/firebase';

// The functions path is ALWAYS same-origin `/.netlify/functions`. We only honor
// VITE_FUNCTIONS_BASE_URL if it is an explicit http(s) URL (e.g. a separate API
// host). Any other value is IGNORED on purpose.
//
// Why so strict: a `/.netlify/functions` value passed through a Git-Bash / MSYS
// shell gets path-converted into a Windows filesystem path like
// `C:/Users/.../Git/.netlify/functions`. If that leaks into the build env
// (VITE_FUNCTIONS_BASE_URL), it bakes into the bundle and every request resolves
// to a bogus file:// path. Ignoring non-http values makes that class of bug
// impossible regardless of how the env var was set.
const FUNCTIONS_PATH = '/.netlify/functions';

function resolveBaseUrl(): string {
  const raw = (import.meta.env.VITE_FUNCTIONS_BASE_URL || '').trim();
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '');
  // Resolve the same-origin path to an absolute URL at runtime.
  if (typeof window !== 'undefined') {
    return new URL(FUNCTIONS_PATH, window.location.origin).toString();
  }
  return FUNCTIONS_PATH;
}

const BASE_URL = resolveBaseUrl();

function assertServedOverHttp(): void {
  if (typeof window !== 'undefined' && window.location.protocol === 'file:') {
    throw new ApiError(
      0,
      'served_from_file',
      'This app is open from a local file (file://). Open it from its deployed ' +
        'https:// URL instead — API calls cannot work from the filesystem.',
    );
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function authHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
}

export async function callFunction<T>(
  name: string,
  options: RequestOptions = {},
): Promise<T> {
  assertServedOverHttp();

  const { method = 'GET', body, query } = options;
  const qs = query
    ? '?' +
      new URLSearchParams(
        Object.entries(query)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)]),
      ).toString()
    : '';

  const headers: Record<string, string> = {
    ...(await authHeader()),
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const url = `${BASE_URL}/${name}${qs}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    // A thrown fetch means the request never reached the server (offline, DNS,
    // blocked local resource, etc.) — surface it as a clear network error.
    throw new ApiError(
      0,
      'network_error',
      `Could not reach the server at ${url}. ${
        networkErr instanceof Error ? networkErr.message : ''
      }`.trim(),
    );
  }

  const isJson = res.headers
    .get('content-type')
    ?.includes('application/json');
  const payload = isJson ? await res.json() : await res.text();

  if (!res.ok || (isJson && payload?.ok === false)) {
    const err = isJson ? payload.error : { code: 'http_error', message: payload };
    throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? 'Request failed');
  }

  return (isJson ? payload.data : payload) as T;
}
